import { Badge, Code, List, Modal, ScrollArea, Stack, Table, Text } from "@mantine/core";

/*
 * "Validation rules" popup for the bulk-settle upload: what each column accepts and what is rejected.
 * Error texts mirror functions-ctc/src/bulkSettle.ts (checkSheetRows / checkRowAgainstSettlement);
 * keep them in sync when a rule changes.
 */

interface Rule {
    column: string;
    rule: string;
    accepted: string[];
    rejected: { value: string; error: string }[];
}

const RULES: Rule[] = [
    {
        column: "File",
        rule: ".xlsx, .xls or .csv, up to 5 MB and 500 rows. The first sheet is read; its header row must keep the template's column names (any order). Empty rows are skipped. In a .csv, dates are read day first like any other text date.",
        accepted: ["The downloaded template, filled in", "Same columns saved as .xls or .csv"],
        rejected: [
            { value: ".pdf / .numbers / .txt, or over 5 MB", error: "Only .xlsx, .xls or .csv files up to 5 MB" },
            { value: "Header \"Settled On\" renamed or removed", error: "Missing column(s): Settled On. Download the template and keep its header row." },
            { value: "501+ rows", error: "The sheet has 501 rows; upload at most 500 at a time" },
        ],
    },
    {
        column: "Reference No",
        rule: "Required. Must exist in the transactions database (upper/lower case doesn't matter).",
        accepted: ["KKC-26-26-00007", "kkc-26-26-00007"],
        rejected: [
            { value: "(empty)", error: "Reference No is missing" },
            { value: "KKC-99-99-99999", error: "Reference not found in the transactions database" },
        ],
    },
    {
        column: "Side",
        rule: "Customer or Broker (any case). One row per side; the same Reference No + Side may appear only once.",
        accepted: ["Customer", "broker", "BROKER"],
        rejected: [
            { value: "Both / Cust / (empty)", error: "Side must be Customer or Broker (got \"Both\")" },
            { value: "Same Reference No + Side on rows 2 and 5", error: "Duplicate of row 2 (shown on row 5)" },
        ],
    },
    {
        column: "Amount",
        rule: "Required. Must equal the commission in the transactions database (to 3 decimals). Keep the value from the template.",
        accepted: ["100", "1,040.500", "125.0004 (database 125)"],
        rejected: [
            { value: "(empty) / abc", error: "Amount is missing or not a number" },
            { value: "120 (database 125)", error: "Amount mismatch (sheet 120, database 125)" },
        ],
    },
    {
        column: "Payment Ref",
        rule: "Required: the bank / core-banking reference of the payment. If there is no reference, write the reason in Comments. Don't leave both empty.",
        accepted: ["TRX-2026-10-0042", "(empty) + Comments filled"],
        rejected: [
            { value: "(empty) and Comments empty", error: "Payment Ref is missing (if there is none, explain why in Comments)" },
            { value: "More than 100 characters", error: "Payment Ref is longer than 100 characters" },
        ],
    },
    {
        column: "Comments",
        rule: "Only required when Payment Ref is empty. Saved on the order with the settlement.",
        accepted: ["Paid in cash at branch", "(empty) when Payment Ref is filled"],
        rejected: [
            { value: "Only spaces, no Payment Ref", error: "Payment Ref is missing (if there is none, explain why in Comments)" },
            { value: "More than 500 characters", error: "Comments is longer than 500 characters" },
        ],
    },
    {
        column: "Settled On",
        rule: "Required. Text dates are always read day first. A time after the date is allowed and ignored. Not after today (Kuwait time) and not before the row's Opened date.",
        accepted: [
            "Excel date cell (any display format)",
            "06/10/2026 · 6/10/2026 · 06-10-2026 · 06.10.2026",
            "2026-10-06",
            "6 Oct 2026 · 06-Oct-2026 · 6 October 2026",
            "06/10/2026 14:30 · 06/10/2026 2:30 PM",
        ],
        rejected: [
            { value: "(empty)", error: "Settled On is missing" },
            { value: "06/10/26 (2-digit year)", error: "Settled On is not a valid date (got \"06/10/26\"); use dd/mm/yyyy" },
            { value: "Oct 6, 2026 (month first)", error: "Settled On is not a valid date (got \"Oct 6, 2026\"); use dd/mm/yyyy" },
            { value: "31/02/2026 (no such day)", error: "Settled On is not a valid date (got \"31/02/2026\"); use dd/mm/yyyy" },
            { value: "A date after today", error: "Settled On is in the future" },
            { value: "05/10/2026 (Opened 06/10/2026)", error: "Settled On (05/10/2026) is before the Opened date (06/10/2026)" },
        ],
    },
    {
        column: "Status in the database",
        rule: "That side must still be unsettled, with its commission calculated.",
        accepted: ["Unsettled side with a calculated commission"],
        rejected: [
            { value: "Already settled", error: "Already settled" },
            { value: "Waived", error: "Commission was waived" },
            { value: "Waiver pending", error: "Waiver awaiting approval" },
            { value: "Commission not calculated", error: "Commission not calculated yet" },
        ],
    },
];

export function CtcBulkSettleRulesModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
    return (
        <Modal opened={opened} onClose={onClose} title="Bulk settle: validation rules" size="80rem">
            <Stack gap="sm">
                <Text size="sm">
                    Every row is checked when you upload; nothing is saved until you confirm. A row is <Badge size="sm" variant="light" color="teal">Matched</Badge> only
                    if it passes all the rules below. Rows with a <Badge size="sm" variant="light" color="red">Problem</Badge> are skipped and show the error from this table.
                </Text>
                <ScrollArea.Autosize mah="65vh">
                    <Table withTableBorder withColumnBorders verticalSpacing="sm" fz="sm" stickyHeader>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th w={130}>Column</Table.Th>
                                <Table.Th w={260}>Rule</Table.Th>
                                <Table.Th>✅ Accepted</Table.Th>
                                <Table.Th>❌ Rejected → error shown</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {RULES.map((r) => (
                                <Table.Tr key={r.column} style={{ verticalAlign: "top" }}>
                                    <Table.Td fw={600}>{r.column}</Table.Td>
                                    <Table.Td>{r.rule}</Table.Td>
                                    <Table.Td>
                                        <List size="sm" spacing={4}>
                                            {r.accepted.map((a) => <List.Item key={a}><Code>{a}</Code></List.Item>)}
                                        </List>
                                    </Table.Td>
                                    <Table.Td>
                                        <Stack gap={6}>
                                            {r.rejected.map((x) => (
                                                <div key={x.value}>
                                                    <Code>{x.value}</Code>
                                                    <Text size="xs" c="red">{x.error}</Text>
                                                </div>
                                            ))}
                                        </Stack>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </ScrollArea.Autosize>
                <Text size="xs" c="dimmed">
                    On upload each matched row also runs the real save against the transactions database and is rolled back, so a row that
                    would fail on confirm shows as a Problem here instead. Between check and confirm, a row can still change (e.g. someone settles
                    it from the Transactions list); it is then reported as not settled.
                </Text>
            </Stack>
        </Modal>
    );
}
